<template>
    <div class="block shadow bg-white rounded px-24 py-28">
        <div>
            <div class="flex-none flex items-start gap-8">
                <svg-icon
                    :class="[interim.linked ? 'text-secondary' : 'text-semantic-warning']"
                    class="w-18 h-18 mt-2"
                    type="link"
                    v-if="interim.linked || interim.connect_mail"
                />
                <svg-icon
                    class="w-18 h-18 text-primary-20 mt-2"
                    type="link-off"
                    v-else
                />

                <p v-if="interim.linked" v-html="$t('interims.connection_status.connected', {interim: interim.name})"></p>
                <p v-else-if="interim.connect_mail" class="leading-relaxed" v-html="$t('interims.connection_status.pending', {interim: interim.name, mail: interim.connect_mail})"></p>
                <p v-else v-html="$t('interims.connection_status.disconnected', {interim: interim.name})"></p>
            </div>
            <div v-if="!interim.linked && interim.connect_mail">
                <div class="flex items-center justify-end gap-8 mt-20">
                    <simple-button
                        :disabled="sendingConnectionRequest && sendingRefreshConnection"
                        icon="replay"
                        look="primary"
                        @click="refreshConnectionRequest"
                    >
                        {{ $t('interims.refresh_connection_request') }}
                    </simple-button>

                    <simple-button
                        :disabled="sendingConnectionRequest && sendingRefreshConnection"
                        display="danger"
                        icon="delete"
                        look="primary"
                        @click="startRemovingConnection"
                    >
                        {{ $t('interims.remove_connection') }}
                    </simple-button>
                </div>
            </div>
        </div>

        <div v-if="interim.linked && interim.connect_mail" class="mt-12">
            <b class="block mb-4">{{ $t('general.more_info') }}</b>
            <p class="leading-relaxed" v-html="$t('interims.connection_status.more_info', {interim: interim.name, mail: interim.connect_mail})"></p>
        </div>

        <div v-if="!interim.linked && !interim.connect_mail && canManage" class="pt-24">
            <simple-button
                v-show="!showRequestForm"
                class="w-full"
                icon="link"
                look="primary"
                @click="openRequestForm"
            >
                {{ $t('interims.send_connection_request') }}
            </simple-button>
        </div>
        <div v-show="showRequestForm">
            <div class="px-24 py-16 rounded-6 bg-primary-5 mb-16">
                <p v-html="$t('interims.connection_request_details', {interim: interim.name})"></p>
            </div>
            <form @submit.prevent="sendConnectionRequest">
                <select-dropdown
                    v-model="company_contact_id"
                    :auto-translate="false"
                    :options="allCompanyUsers"
                    :placeholder="$t('interims.choose_user')"
                    class="mb-16"
                    searchable
                    :input-label="$t('interims.add_user_to_connection_request')"
                />

                <simple-input
                    class="mb-16"
                    disabled
                    :input-label="$t('interims.email')"
                    placeholder="john.doe@example.com"
                    required
                    readonly
                    ref="connectMail"
                    type="email"
                    v-model="email"
                />


                <p class="mb-4 text-body-sm flex items-center first-letter:uppercase font-semibold">
                    {{ $t('interims.connection_language') }}
                </p>
                <div class="flex flex-wrap mb-24">
                    <radio-button
                        class="mr-8 mb-4"
                        :key="lang.value"
                        :value="lang.value"
                        v-model="connect_lang"
                        v-for="lang in languages"
                    >
                        {{ $t(lang.label) }}
                    </radio-button>
                </div>


                <div class="flex items-center justify-end gap-8">
                    <simple-button look="text" @click="showRequestForm = false;">{{ $t('general.cancel') }}</simple-button>
                    <simple-button
                        :loading="sendingConnectionRequest"
                        display="primary"
                        look="primary"
                        type="submit"
                    >{{ $t('general.submit') }}
                    </simple-button>
                </div>
            </form>
        </div>

        <popup-modal-confirm
            v-if="removingConnection"
            :message="$t('interims.popup_connection_remove.message')"
            show-exclamation
            :title="$t('interims.popup_connection_remove.title')"
            :working="performingDeleteConnection"
            @close="stopRemovingConnection"
            @confirm="removeConnectionRequest"
        />
    </div>
</template>

<script>
import Interim from '../models/Interim';
import Input from './forms/Input.vue';
import SimpleButton from './ui/SimpleButton.vue';
import SvgIcon from './ui/SvgIcon.vue';
import * as interims from '../services/InterimService';
import {normalizeJson} from '../../shared/helpers';
import {ADD_NOTIFICATION} from '../state/mutation-types';
import {mapState, mapActions} from 'pinia';
import {useRepo} from 'pinia-orm';
import {useMainStore} from '../state/store';
import {useAuthenticationStore} from '../state/modules/authentication';
import {useNotificationsStore} from '../state/modules/notifications';
import {useUsersStore} from '../state/modules/users';
import SelectDropdown from './forms/SelectDropdown';
import PopupModalConfirm from './ui/PopupModalConfirm';
import Loading from './ui/Loading';
import Toggle from './forms/Toggle';
import Form from '../etc/forms/Form';
import RadioButton from './forms/RadioButton';

export default {
    components: {
        RadioButton,
        Toggle,
        'simple-button': SimpleButton,
        'svg-icon': SvgIcon,
        'simple-input': Input,
        'loading': Loading,
        SelectDropdown,
        PopupModalConfirm,
    },
    props: {
        interim: Interim,
        canManage: Boolean,
    },
    data: function () {
        return {
            email: this.interim.contact_mail ?? null,
            showRequestForm: false,
            sendingConnectionRequest: false,
            performingDeleteConnection: false,
            sendingRefreshConnection: false,
            removingConnection: false,
            company_contact_id: null,
            // connect_id: (this.interim.connectUsers.length ? this.interim.connectUsers.find(user => user.default).id : null),
            languages: this.languages,
            connect_lang: this.connectLang,
        };
    },
    computed: {
        ...mapState(useMainStore, ['availableLanguages']),
        ...mapState(useAuthenticationStore, ['currentUserId', 'currentUser']),
        ...mapState(useUsersStore, ['allUsers']),
        languages() {
            return this.availableLanguages;
        },
        connectLang() {
            return this.currentUser.language;
        },
        allCompanyUsers() {
            return this.allUsers.map(user => ({
                value: user.id,
                label: user.name,
            }));
        },
        // allConnectUsers() {
        //
        //It has been decided to remove the code that makes it possible to add multiple contacts for interim offices.
        //With this it was possible to have multiple contacts, but in the end you could not do anything with that.
        //A connection can only be made with one email address with which the vacancy can be shared.
        //If this is still not needed 1 year from now fully delete this code
        //
        //     if (!this.interim.connectUsers.length) {
        //         return;
        //     }
        //
        //     return this.interim.connectUsers.filter(items => items.email !== '').map(user => ({
        //         value: user.id,
        //         label: (user.name ? user.name + ' - ' : '') + user.email,
        //     }));
        // },
        // Selecting a default contact in the users, was to many steps and unnecessary and complicated so i needed to remove this.
        // If this is still not required in 1 year from now delete this code fully (search on default_for_external)
        // companyExternalContact: {
        //     get() {
        //         let id = this.currentUserId;
        //
        //         let companyUser = this.allUsers.find(user => user.companyUsers.find(cu => cu.company_id === this.$store.state.authentication.currentCompanyId && cu.default_for_external));
        //
        //         if (companyUser) {
        //             id = companyUser.id;
        //         }
        //
        //         return this.company_contact_id || id;
        //     },
        //     set(val) {
        //         this.company_contact_id = val;
        //     },
        // },
    },
    methods: {
        ...mapActions(useUsersStore, ['fetchUsers']),
        ...mapActions(useNotificationsStore, [ADD_NOTIFICATION]),
        openRequestForm() {
            this.showRequestForm = true;
            this.$refs.connectMail.focus();
        },
        //Send initial connection request
        sendConnectionRequest() {
            stopHsTracking();
            // let connect_user = this.interim.connectUsers.find(user => user.id == this.connect_id);
            //
            // if (!connect_user.email) {
            if (!this.email) {
                this[ADD_NOTIFICATION]({
                    type: 'error',
                    message: this.$t('interims.email_required'),
                });
                return;
            }

            this.sendingConnectionRequest = true;

            interims.update(this.interim.id, {
                data: {
                    attributes: {
                        // 'connect_mail': connect_user.email,
                        'connect_mail': this.email,
                        'company_contact_id': this.company_contact_id,
                        'connect_lang': this.connect_lang,
                    },
                },
            }).then(result => {
                return useRepo(Interim).save(normalizeJson(result));
            }).then(() => {
                this.showRequestForm = false;
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('interims.connection_request_sent', {
                        interim: this.interim.name,
                        // email: connect_user.email,
                        email: this.email,
                    }),
                });
            }).finally(() => {
                this.sendingConnectionRequest = false;
                this.email = '';

                startHsTracking();
            });
        },
        //Open confirm popup to remove connection
        startRemovingConnection() {
            this.removingConnection = true;
        },
        //Close confirm popup to remove connection
        stopRemovingConnection() {
            this.removingConnection = false;
        },
        //Remove actual connection
        removeConnectionRequest() {
            this.performingDeleteConnection = true;

            //Remove connection
            interims.destroyConnection(this.interim.id).then(result => {
                return useRepo(Interim).save(normalizeJson(result));
            }).then(() => {
                this.showRequestForm = false;
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('interims.connection_request_removed', {
                        interim: this.interim.name,
                    }),
                });
            }).finally(() => {
                this.performingDeleteConnection = false;
                this.removingConnection = false;
            });
        },
        //Refresh the interim connection
        refreshConnectionRequest() {
            this.sendingRefreshConnection = true;

            interims.refreshConnection(this.interim.id).then(result => {
                return useRepo(Interim).save(normalizeJson(result));
            }).then(() => {
                this.showRequestForm = false;
                this[ADD_NOTIFICATION]({
                    type: 'success',
                    message: this.$t('interims.connection_request_sent', {
                        interim: this.interim.name,
                        email: this.interim.connect_mail,
                    }),
                });
            }).finally(() => {
                this.sendingRefreshConnection = false;
            });
        },
    },
    watch: {
        'interim.contact_mail'() {
            this.email = this.interim.contact_mail;
        }
    },
    mounted() {
        //Fetch all the users
        this.fetchUsers();

        //Set connect user to the correct loggedin user
        this.company_contact_id = this.currentUserId;
    },
};
</script>
